/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Injectable, Injector } from "@angular/core";
import { Logger, clientUtil, EventBusService, BusEvent } from "@html-core";

import { BlastWmks } from "../../../shared/desktop/common/blast-wmks.service";
import { BlastOption } from "../../../shared/desktop/wmks/blast-option";
import { KeyboardSettingService } from "../../../shared/common/keyboard-setting/keyboard-setting.service";
import { Localize } from "../../../shared/common/service/localize.service";
import { ChromeBlastSessionInstance } from "../../../shared/desktop/wmks/chrome-blast-session.instance";
import { MultimonService } from "../../../shared/desktop/multimon/main-page/multimon.service";
import {
   RemoteSessionEventService,
   SessionMsg
} from "../../../shared/common/remote-session/remote-session-event.service";
import { UrlParameterService } from "../../../chrome-client/desktop/blast-common/url-parameter.service";
import { SessionLifeCycleService } from "../../../chrome-client/desktop/blast-common/session-life-cycle.service";
import { SessionSettingService } from "../../../native-shared/desktop/session-setting.service";
import { SessionSyncService } from "./session-sync.service";
import { IwaRXService } from "./iwa-rx.service";
import { SDKBlastService } from "../../../chrome-client/SDK/sdk-blast-service";

@Injectable()
export class DesktopBlastController extends SessionLifeCycleService {
   private _triedSSLVerify: boolean = false;
   private _blastSession: BlastWmks = null;

   constructor(
      private urlParameterService: UrlParameterService,
      private keyboardSettingService: KeyboardSettingService,
      private localize: Localize,
      private injector: Injector,
      private multimonService: MultimonService,
      private remoteSessionEventService: RemoteSessionEventService,
      private eventBusService: EventBusService,
      private sessionSettingService: SessionSettingService,
      private sessionSync: SessionSyncService,
      private _rxService: IwaRXService,
      private sdkBlastService: SDKBlastService
   ) {
      super();
      this.eventBusService
         .listen(BusEvent.DPIDataSyncSuccessEvent.MSG_TYPE)
         .subscribe((msg: BusEvent.DPIDataSyncSuccessEvent) => {
            this.updateWMKSDPI(msg.wmksKey, msg.remoteScaleDPI);
         });
   }

   public updateWMKSDPI = (key: string, dpi: number) => {
      if (key !== this._blastSession.key) {
         return;
      }
      this._blastSession.updateDPIInfo(dpi);
   };

   public init = () => {
      // Check the parameter first
      if (this.isInValidURL() || this._isInvalidBlastURL()) {
         Logger.debug("invalid parameter to bootstrap the blast session");
         return;
      }

      document.title = "desktop:" + this.urlParameterService.desktopName;

      Logger.info("init SDK for blast desktop session");
      this.sdkBlastService.init(this.urlParameterService);

      this.sessionSettingService.init(this.urlParameterService.sessionKey, this.urlParameterService.launchSettings);
      this._rxService.init(this.urlParameterService.sessionKey);
      this.sessionSync.init(this.urlParameterService.sessionKey, this);

      this._initBlastSession();

      this.localize.setLocaleAndLoad(this.urlParameterService.acceptedLanguage);
   };

   public updateResolution = (resolution) => {
      this.sessionSettingService.setResolution(resolution);
   };

   private _initBlastSession = () => {
      Logger.info("connecting to blast session" + this.urlParameterService.sessionKey);

      let opt: BlastOption = {
         wmksService: this,
         key: this.urlParameterService.sessionKey,
         isApp: this.urlParameterService.isApplicationSession,
         isShadow: this.urlParameterService.isShadow,
         name: this.urlParameterService.desktopName,
         reconnectToken: null,
         triedSSLVerify: this._triedSSLVerify,
         isMultiSession: this.urlParameterService.isMultiSession,
         enableUsb: this.urlParameterService.enableUsb,
         usbTicket: this.urlParameterService.usbTicket
      };

      this._blastSession = new ChromeBlastSessionInstance(this.injector, opt);
      this._blastSession.entitleId = this.urlParameterService.entitleId;
      this._blastSession.sessionId = this.urlParameterService.sessionId;

      const webSocketUrl = this.urlParameterService.blastURL.replace("http", "ws");
      let connectOptions = this.urlParameterService.connectOptions;

      this._blastSession.initialize(webSocketUrl, connectOptions);

      this._keyMappingSetting();
   };

   private _keyMappingSetting = () => {
      if (this.urlParameterService.launchSettings["keyMappingSetting"]) {
         this._blastSession.wmks(
            "updateKeyMappingSetting",
            this.keyboardSettingService.getFinalKeyMappingSetting(
               JSON.parse(this.urlParameterService.launchSettings["keyMappingSetting"])
            )
         );
      }
   };

   public isInValidURL = () => {
      return this.urlParameterService.isApplicationSession;
   };

   private _isInvalidBlastURL = () => {
      return (
         !this.urlParameterService.sessionKey ||
         !this.urlParameterService.blastURL ||
         !this.urlParameterService.launchSettings
      );
   };

   public sessionOnConnecting = (wmksSession) => {
      this.remoteSessionEventService.emit(SessionMsg.SESSION_CONNECTING_MSG, wmksSession);
   };

   public checkSessionInMultimon = (key: string, callback: Function) => {
      this.multimonService.checkSessionInMultimon(key, callback);
   };

   public frameBufferInited = (agentDPI) => {
      Logger.info("frame buffer inited with DPI" + agentDPI);
   };

   public sessionOnConnected = (wmksSession) => {
      this.remoteSessionEventService.emit(SessionMsg.SESSION_CONNECTED_MSG, wmksSession);
      this._blastSession.setVisibility(true);
   };

   public sessionOnDisconnected = (wmksSession, code) => {
      this.remoteSessionEventService.emit(SessionMsg.SESSION_DISCONNECTED_MSG, wmksSession);
   };

   public sessionOnRemoved = (wmksSession) => {
      this.remoteSessionEventService.emit(
         SessionMsg.SESSION_REMOVED_MSG,
         wmksSession.key,
         wmksSession.isApplicationSession
      );
   };

   public changeMonitorStatus = (status) => {
      this.sessionSettingService.clientStatus.isInMultimon = status;
   };

   public onResolutionUpdated = (session, agentPixel) => {
      if (session !== this._blastSession) {
         Logger.error("inner error, the blast session mismatches");
      }
      // Used to bypass issues
      let targetPixel = clientUtil.getWindowPixels();
      let screenSize = clientUtil.getWindowResolution();
      Logger.info(
         "Agent Resolution: " + JSON.stringify(agentPixel) + "\nexpected resolution: " + JSON.stringify(targetPixel)
      );

      // Compatible with Agents that need resolution adjustment after connection
      if (Math.abs(targetPixel[0] - agentPixel.width) <= 1 || Math.abs(targetPixel[1] - agentPixel.height) <= 1) {
         Logger.info("resolution inited successfully");
         session.updateResolution(screenSize, false);
      } else {
         Logger.info("set resolution again, since resolution initialize failed");
         session.updateResolution(screenSize, true);
      }
   };

   public addMonitor = (uiCallback, screenSetting) => {
      let currentSession = this._blastSession;
      let options = {
         // enableH264: isMP4Supported() && isMP4Enabled
         enableH264: false
      };

      let closeSidebar = function () {};

      this.multimonService.addMonitor(
         currentSession,
         currentSession.wmksContainer[0],
         uiCallback,
         closeSidebar,
         this._handleResize,
         options,
         screenSetting
      );

      $("#canvas-container").css("overflow", "visible");
   };

   public hasMultimonCapacity = () => {
      if (!this._blastSession) {
         return false;
      }
      return this._blastSession.multimonServerEnabled;
   };

   public enterMultimon = () => {
      $(window).off("resize", this._handleResize);
      const currentSession = this._blastSession;
      if (currentSession) {
         Logger.info("enter multimon for session " + currentSession.key);
         this.changeMonitorStatus(true);
         this.multimonService.enterMultimonMode();
      }
   };

   public isCurrentSessionPerMonitorDPI = () => {
      return this._blastSession.isDisplayScaleDisabled();
   };

   public disconnectEventConnections = () => {
      this._blastSession.wmks("disconnectEvents");
      $(window).unbind("mousedown.wmks");
      $(window).unbind("mousewheel.wmks");
      $(window).unbind("mousemove.wmks");
      $(window).unbind("mouseup.wmks");
   };

   private _handleResize = (e) => {
      let screenSize = clientUtil.getWindowResolution();
      if (this._blastSession !== this.multimonService.getMultimonSession()) {
         this._blastSession.updateResolution(screenSize, true);
      } else {
         Logger.debug("skip resize for session wmksKey: " + this._blastSession.key);
      }
   };

   public recoveryEventConnections = () => {
      try {
         this._blastSession.wmks("connectEvents");
      } catch (e) {
         Logger.debug("fail to reconnect wmks events, please check the network");
      }
   };

   public preparingForMultimon = () => {
      Logger.info("preparing for entering multimon");
      this.multimonService.preparingForMultimon();
   };

   public showError = (wmksSession, title, message) => {
      this.remoteSessionEventService.emit(
         SessionMsg.SESSION_MODAL_MSG,
         wmksSession.key,
         wmksSession.isApplicationSession,
         {
            type: "Error",
            data: {
               title: title,
               errorMessage: message
            }
         }
      );
   };

   public quitMultimon = () => {
      this.multimonService.cancelForMultimon();
      this.multimonService.switchToSingleMon(this._blastSession);
   };

   public setCanvasVisibility = (wmksKey, isShow) => {
      this._blastSession.setCanvasVisibility(isShow);
   };

   public updateReconnectToken = (wmksSession, reconnectToken) => {
      this.remoteSessionEventService.emit(
         SessionMsg.SESSION_TOKEN_UPDATED_MSG,
         wmksSession.key,
         wmksSession.isApplicationSession,
         reconnectToken
      );
   };

   public getCurrentSession = (): BlastWmks => {
      return this._blastSession;
   };

   public setLastUserActivityTime = () => {};

   public updateTriedSSLVerify = async () => {
      // on of condition to show shelf icon since we should not show it when accepting cert
      return null;
   };
}
